import * as THREE from "three";
import { spinTable } from "./spinTable";
import { SpinDriver } from "./spinDriver";

/** A clip's first key time (cached): 0 for the zeroed clips, 1/30 s for one exported from Blender frame 1. */
function loopStart(c: THREE.AnimationClip): number {
  let t0 = starts.get(c);
  if (t0 === undefined) {
    t0 = c.tracks.length ? Math.min(...c.tracks.map((t) => t.times[0] ?? 0)) : 0;
    t0 = t0 < c.duration ? t0 : 0;
    starts.set(c, t0);
  }
  return t0;
}
const starts = new WeakMap<THREE.AnimationClip, number>();

/**
 * A placed instance of an HQ model with its baked glTF clips (art/hq/README.md "Animation"): one AnimationMixer per
 * instance, clips by name, one-shots return to the looping clip when they finish. Models without clips get a rig
 * whose calls are no-ops, so the views never branch on it.
 */
export class AnimRig {
  private readonly mixer: THREE.AnimationMixer | null;
  private loop: THREE.AnimationAction | null = null;
  private loopName = "";
  private oneShot: THREE.AnimationAction | null = null;
  private onOneShotDone: (() => void) | null = null;
  /** A `blend` one-shot's cross-fade (s): it holds its last frame and cross-fades back to the loop (0: the old cut). */
  private oneShotBlend = 0;
  /** The one-shot playing cuts the loop (`solo`): a loop changed under it waits at weight 0 until it ends. */
  private oneShotSolo = false;
  /**
   * A node the view poses on top of the clips (a tower's aimed weapon: views/towerModel.ts weaponOverlay), taken back
   * to the clips' pose while an action starts and re-posed after. The mixer saves a node's pose as its "original" when
   * the first action that keys it starts, blends that in while the action fades in and writes it back when the action
   * ends: saved aimed, a shot's `fire` ended with the weapon at its aim at the shot PLUS the aim on top — it swung off
   * by up to half a turn until the next shot (owner report 2026-10-13: "they snap").
   */
  overlay: { clear(): void; reapply(): void } | null = null;

  /** The parts that turn round and round (spinTable.ts): turned by code, their turn taken off the clips. */
  private readonly spin: SpinDriver | null;
  private readonly clips: readonly THREE.AnimationClip[];

  constructor(readonly root: THREE.Object3D, clips: readonly THREE.AnimationClip[]) {
    const table = spinTable(clips);
    this.clips = table.clips;
    this.spin = table.spinners.length > 0 ? new SpinDriver(root, table.spinners) : null;
    this.mixer = clips.length > 0 ? new THREE.AnimationMixer(root) : null;
    this.mixer?.addEventListener("finished", (e) => {
      if (e.action !== this.oneShot) return;
      const done = this.onOneShotDone, blend = this.oneShotBlend;
      this.oneShot = null;
      this.onOneShotDone = null;
      this.oneShotBlend = 0;
      this.oneShotSolo = false;
      // (full weight first: a loop changed under a solo one-shot waited at weight 0, see playLoop)
      if (blend > 0) { e.action.fadeOut(blend); if (this.loop) { this.loop.enabled = true; this.start(this.loop.setEffectiveWeight(1).fadeIn(blend)); } }
      else if (e.action === this.returning) this.backToLoop(e.action);
      done?.();
    });
  }

  has(name: string): boolean { return THREE.AnimationClip.findByName(this.clips as THREE.AnimationClip[], name) !== null; }

  /**
   * Cross-fades to a looping clip (`idle`, `walk`) over `fade` s; no-op when it is already playing or the model lacks
   * it (a fortress companion's flap ↔ glide takes a long `fade`, so the wings settle rather than snap).
   */
  playLoop(name: string, timeScale = 1, randomStart = false, fade = AnimRig.LoopFadeSeconds): void {
    if (!this.mixer) return;
    if (this.loopName === name) { if (this.loop) this.loop.timeScale = timeScale; return; }
    const clip = THREE.AnimationClip.findByName(this.clips as THREE.AnimationClip[], name);
    if (!clip) return;
    const next = this.mixer.clipAction(clip);
    next.setLoop(THREE.LoopRepeat, Infinity);
    next.clampWhenFinished = false;
    next.enabled = true;
    next.timeScale = timeScale;
    next.reset();
    if (randomStart) next.time = Math.random() * clip.duration;
    if (this.oneShot && this.oneShotSolo) {
      // a solo one-shot holds the body (a raider's blow as it halts at a soldier: walk → idle under the swing): the new
      // loop waits under it at weight 0 and fades in when it ends, rather than fading in over it and halving it
      if (this.loop && this.loop !== next) this.loop.stop();
      next.setEffectiveWeight(0);
      this.start(next);
    } else if (this.loop && this.loop !== next) { this.start(next); this.loop.crossFadeTo(next, fade, false); }
    else this.start(next.fadeIn(0.1));
    this.loop = next;
    this.loopName = name;
  }

  /**
   * Plays a one-shot (`fire`, `build`, `hit`, `attack`, `die`, `open`). `hold` keeps the last frame (`die`); otherwise
   * the loop resumes when it finishes. Returns the clip length in seconds (0 when the model lacks it). `startAt` seconds
   * skips into the clip.
   *
   * `timeScale` plays it faster (a tower's `fire` fitted to its fire interval). `solo` fades the loop out while it
   * plays: the mixer averages two actions of equal weight, so a `fire` over an `idle` that also keys the weapon played
   * at half strength — half the cannon's recoil, and the idle's look-around yaw swinging the barrel off its target.
   * `blend` (s, with `solo`) cross-fades the loop out and the one-shot in over that time, holds the one-shot's last
   * frame and cross-fades back to the loop (not reset) over the same time: no pop at either end (a flying companion's
   * breath, whose wings must not snap from mid-beat to the clip's first frame and back).
   */
  playOnce(name: string, hold = false, onDone: (() => void) | null = null, { timeScale = 1, solo = false, startAt = 0, blend = 0 }: { timeScale?: number; solo?: boolean; startAt?: number; blend?: number } = {}): number {
    if (!this.mixer) return 0;
    const clip = THREE.AnimationClip.findByName(this.clips as THREE.AnimationClip[], name);
    if (!clip) return 0;
    const a = this.mixer.clipAction(clip);
    a.setLoop(THREE.LoopOnce, 1);
    // every one-shot ends on its last frame and hands back with a cross-fade (backToLoop), so the loop never fades in
    // from the rest pose: a raider coming out of a hit used to sweep its feet through the bind pose for 0.12 s
    a.clampWhenFinished = true;
    this.returning = hold || blend > 0 ? null : a;
    a.enabled = true;
    a.reset();
    a.timeScale = timeScale;
    // `startAt` enters the clip part-way: a troop's blow that the view could not see coming starts at its impact frame
    if (startAt > 0) a.time = Math.min(startAt, clip.duration);
    // a solo one-shot that cuts another (a troop's block over its fidget) takes over rather than averaging with it
    if (solo && this.oneShot && this.oneShot !== a) { this.oneShot.stop(); this.onOneShotDone = null; }
    const fade = blend > 0 ? blend : AnimRig.SoloFadeSeconds;
    if (hold) { this.loop?.stop(); this.loop = null; this.loopName = ""; }
    else if (solo) this.loop?.fadeOut(fade);
    this.start(a.fadeIn(fade));
    this.oneShot = a;
    this.onOneShotDone = onDone;
    this.oneShotBlend = hold ? 0 : blend;
    this.oneShotSolo = solo && !hold;
    return clip.duration;
  }

  /** The one-shot that cross-fades back to the loop when it ends (not a `hold` nor a `blend` one, which do their own). */
  private returning: THREE.AnimationAction | null = null;

  /**
   * A finished one-shot (held on its last frame) cross-fades back to the loop over ReturnFadeSeconds. The loop is not
   * reset: it carries on at its own phase (a walk posed by distance, an idle mid-breath). A loop that ran under the
   * one-shot at full weight just takes over; one faded out under a `solo` one fades back in.
   */
  private backToLoop(a: THREE.AnimationAction): void {
    this.returning = null;
    const f = AnimRig.ReturnFadeSeconds;
    a.fadeOut(f);
    const loop = this.loop;
    if (!loop) return;
    loop.enabled = true;
    if (loop.getEffectiveWeight() < 1) this.start(loop.stopFading().setEffectiveWeight(1).fadeIn(f));
    else if (!loop.isScheduled()) this.start(loop);
  }

  /** Starts an action with any overlay lifted, so the mixer saves the clips' pose (see `overlay`). */
  private start(a: THREE.AnimationAction): void {
    this.overlay?.clear();
    a.play();
    this.overlay?.reapply();
  }

  /** A one-shot's fade in, and a `solo` one's fade of the loop under it (seconds). */
  static readonly SoloFadeSeconds = 0.05;
  /** A finished one-shot's cross-fade back to the loop (seconds). */
  static readonly ReturnFadeSeconds = 0.12;
  /** A change of looping clip's cross-fade (seconds) unless the caller asks for another. */
  static readonly LoopFadeSeconds = 0.15;

  get busy(): boolean { return this.oneShot !== null; }

  /** Seconds into the one-shot playing now (its own clip time, after its time scale), or -1 when none plays. */
  get oneShotTime(): number { return this.oneShot ? this.oneShot.time : -1; }

  /** Name of the looping clip playing now ("" when none). */
  get loopClip(): string { return this.loopName; }

  /** A clip's length in seconds, 0 when the model lacks it. */
  clipLength(name: string): number {
    return THREE.AnimationClip.findByName(this.clips as THREE.AnimationClip[], name)?.duration ?? 0;
  }

  /** Where the looping clip is in its cycle, 0–1 (the walk's footfalls drive the unit squash); 0 without a loop. */
  loopPhase(): number {
    const a = this.loop;
    if (!a) return 0;
    const c = a.getClip(), t0 = loopStart(c), span = c.duration - t0;
    return span > 0 ? (((a.time - t0) % span) + span) % span / span : 0;
  }

  /**
   * Holds the looping clip at `phase` (cycles; the fraction is used) instead of letting the mixer run it: a walk driven
   * by the distance its unit covers (views/enemyGait.ts). A clip exported from frame 1 (its first key at 1/30 s) is
   * spanned from that key, so the loop does not stall on it.
   */
  seekLoop(phase: number): void {
    const a = this.loop;
    if (!a) return;
    const c = a.getClip(), t0 = loopStart(c);
    // the loop it poses must be live: one left disabled or unscheduled (faded out to nothing under a cross-fade, or
    // stopped behind a one-shot) would hold the model at its rest pose while the unit glides on
    if (!a.enabled || !a.isScheduled()) { a.enabled = true; a.setEffectiveWeight(1); a.play(); }
    a.timeScale = 0;
    a.time = t0 + (phase - Math.floor(phase)) * (c.duration - t0);
  }

  update(dt: number): void {
    this.mixer?.update(dt);
    const loop = this.loop;
    this.spin?.step(dt, { loop, loopName: this.loopName, seekPhase: loop && loop.timeScale === 0 ? this.loopPhase() : null, oneShot: this.oneShot });
  }

  /**
   * Ends the one-shot playing now and cross-fades back to the loop (backToLoop), its `onDone` fired: a unit that walks
   * on before its cast or blow has played out (views/enemyFeel.ts) would otherwise drag its feet in the one-shot's
   * standing pose. A `hold` or `blend` one-shot plays out.
   */
  release(): void {
    const a = this.oneShot;
    if (!a || a !== this.returning) return;
    const done = this.onOneShotDone;
    this.oneShot = null; this.onOneShotDone = null; this.oneShotBlend = 0; this.oneShotSolo = false;
    this.backToLoop(a);
    done?.();
  }

  /** Skips a held one-shot to its last frame (the chest's tap-to-skip); its `onDone` is not fired. */
  jumpToEnd(): void {
    const a = this.oneShot;
    if (!a || !this.mixer) return;
    this.onOneShotDone = null;
    a.time = a.getClip().duration;
    this.mixer.update(0);
    a.paused = true;
  }

  /** Stops everything and drops the actions (pooled views call this before reuse). */
  reset(): void {
    if (!this.mixer) return;
    this.mixer.stopAllAction();
    this.spin?.reset();
    this.loop = null; this.loopName = ""; this.oneShot = null; this.onOneShotDone = null; this.oneShotSolo = false;
  }
}
